import { GroupConsoleTitle } from '../common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@framework/ui/atoms/Button';
import { useCurrentUserId } from '@framework/auth/authStatus';
import { KeysRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { Link, useNavigate } from 'react-router';
import { UserIcon } from '@framework/ui/atoms/UserIcon';
import type { GroupId, UserId } from 'balus-common';
import { ItemMenuButton } from './ItemMenuButton';
import { GroupMemberRemoveModal } from './GroupMemberRemoveModal';
import { IndeterminateCheckbox } from './IndeterminateCheckbox';
import { FullScreenLoading } from '@framework/ui';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { RoleSelect } from './RoleSelect';
import { type GroupMember, useGroupMembers } from '@group/hooks/useGroupMembers';
import { useGroupMemberAIUsages } from '@group/hooks/useGroupMemberAIUsages';
import { useGroupConsoleOutletContext } from '../WithGroupConsoleMenu';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { trpcClient } from '@framework/trpcClient';
import { classNames } from '@framework/utils';

type MemberItem = {
  memberData: GroupMember;
  workspacesCount: number;
};

const renderAIUsageRatio = (usedRatio: number | null | undefined): React.ReactNode => {
  if (usedRatio === null || usedRatio === undefined) return '-';

  const className = usedRatio >= 1 ? 'text-red-700' : usedRatio >= 0.8 ? 'text-yellow-700' : '';
  // 上限超過分は出さない。メンバー本人が見る AIUsageDonutChart も100%で頭打ちにしている
  return <span className={className}>{Math.min(Math.floor(usedRatio * 100), 100)}%</span>;
};

const getAssignedWorkspaceCount = async (memberId: UserId, groupId: GroupId): Promise<{ [key: UserId]: number }> => {
  const repo = new KeysRepository(RTDBPath.Workspace.assignedGroupWorkspaceIndexPath(memberId, groupId));
  const result = await repo.get();
  return { [memberId]: result.length };
};

export const GroupConsoleMembersPage: React.FC = () => {
  const { t } = useTranslation();
  const { group } = useGroupConsoleOutletContext();
  const currentUserId = useCurrentUserId();
  const [keyword, setKeyword] = useState<string>('');
  const [allMemberItems, setAllMemberItems] = useState<MemberItem[]>([]);
  const [removableMemberData, setRemovableMemberData] = useState<GroupMember | null>(null);
  const navigate = useNavigate();

  const { members: groupMembers } = useGroupMembers(group.id);

  useEffect(() => {
    if (groupMembers === null) return;

    const getMemberItems = async () => {
      if (groupMembers === null) return [];
      const memberIds = groupMembers.map((member) => member.id);

      // 所属ワークスペース数の取得
      const assignedWorkspaceCounts = (
        await Promise.all(memberIds.map((memberId) => getAssignedWorkspaceCount(memberId, group.id)))
      ).reduce((result, i) => {
        return {
          ...result,
          ...i,
        };
      }, {});

      return groupMembers.map((memberData) => ({
        memberData,
        workspacesCount: assignedWorkspaceCounts[memberData.id] || 0,
      }));
    };

    getMemberItems().then((memberItems) => {
      setAllMemberItems(memberItems);
    });
  }, [group, groupMembers]);

  const handleChangeKeyword = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    if (!e.target.value) {
      setKeyword('');
    }
  }, []);

  const handleCloseGroupMemberRemoveModal = useCallback(() => {
    setRemovableMemberData(null);
  }, []);

  const memberItems = useMemo(() => {
    if (!keyword) return allMemberItems;

    return allMemberItems.filter(
      (i) =>
        i.memberData.name.toLocaleLowerCase().includes(keyword.toLocaleLowerCase()) ||
        i.memberData.mailAddress.toLocaleLowerCase().includes(keyword.toLocaleLowerCase())
    );
  }, [allMemberItems, keyword]);

  const handleClickAddMemberButton = useCallback(() => {
    navigate(UserPagePathBuilder.groupConsoleInvitePage(group.id));
  }, [navigate, group.id]);

  const guestCount = allMemberItems.filter(({ memberData }) => memberData.role === 'guest').length;

  // グループ単位のAIアシスタント有効フラグ。ONのときだけメンバー別の列を表示する
  const [aiAssistantEnabled] = useSnapshot({
    path: RTDBPath.Group.aiAssistantEnabledPath(group.id),
    load: ({ snapshot }) => !!snapshot.val(),
  });

  // メンバー別のAIアシスタント許可マップ。キーが存在するuserId=ON（NoAssign設計）
  const [memberEnabledKeys] = useSnapshot({
    path: RTDBPath.Group.aiAssistantMemberEnabledEntriesPath(group.id),
    load: ({ getChildValues }) => Object.keys(getChildValues()),
  });
  const memberEnabledSet = useMemo(() => new Set(memberEnabledKeys ?? []), [memberEnabledKeys]);

  const { usageRatios: memberAIUsageRatios, failed: aiUsageLoadFailed } = useGroupMemberAIUsages(group.id, {
    skip: !aiAssistantEnabled,
  });

  const [isSubmittingAI, setIsSubmittingAI] = useState(false);

  const submitAIAssistantSettings = useCallback(
    async (settings: { userId: UserId; enabled: boolean }[]) => {
      if (settings.length === 0) return;
      setIsSubmittingAI(true);
      try {
        await trpcClient.group.updateAIAssistantMemberEnabled.mutate({ groupId: group.id, settings });
        toast.success(t('group.members.modelingAgentToggle.success'));
      } catch (err) {
        toast.error(t('group.members.modelingAgentToggle.error'));
        console.error(err);
      } finally {
        setIsSubmittingAI(false);
      }
    },
    [group.id, t]
  );

  // ヘッダーの一括チェックは「表示中（検索フィルタ適用後）のメンバー」のみを対象にする
  const displayedMemberIds = useMemo(() => memberItems.map((i) => i.memberData.id), [memberItems]);
  const enabledDisplayedCount = displayedMemberIds.filter((id) => memberEnabledSet.has(id)).length;
  const allDisplayedEnabled = displayedMemberIds.length > 0 && enabledDisplayedCount === displayedMemberIds.length;
  const someDisplayedEnabled = enabledDisplayedCount > 0 && enabledDisplayedCount < displayedMemberIds.length;

  const handleToggleAllAIAssistant = useCallback(() => {
    // 全員ONなら全員OFFへ、それ以外は全員ONへ
    const enabled = !allDisplayedEnabled;
    submitAIAssistantSettings(displayedMemberIds.map((userId) => ({ userId, enabled })));
  }, [allDisplayedEnabled, displayedMemberIds, submitAIAssistantSettings]);

  const handleToggleMemberAIAssistant = useCallback(
    (userId: UserId, enabled: boolean) => {
      submitAIAssistantSettings([{ userId, enabled }]);
    },
    [submitAIAssistantSettings]
  );

  if (!groupMembers) return <FullScreenLoading />;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <GroupConsoleTitle
          title={t('group.members.label')}
          subtitle={
            allMemberItems.length
              ? guestCount
                ? t('group.members.page.subtitle', { count: allMemberItems.length, guestCount })
                : t('group.members.page.subtitleNoGuest', { count: allMemberItems.length })
              : ''
          }
        />
        <div className="relative flex w-3/5">
          <div className="mr-4 flex min-w-0 flex-1">
            <input
              value={keyword}
              onChange={handleChangeKeyword}
              type="text"
              className="min-w-0 flex-1 rounded-md border-2 border-gray-400 py-1 pr-2 placeholder:text-sm focus:outline-none"
              placeholder={t('common.action.search')}
              style={{ paddingInlineStart: '24px' }}
            />
          </div>
          <Button color="brand" onClick={handleClickAddMemberButton}>
            {t('group.members.addButton')}
          </Button>
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute flex-none text-gray-600"
            style={{ top: '12px', left: '6px' }}
          />
        </div>
      </div>
      {aiUsageLoadFailed && <div className="mb-2 text-sm text-red-700">{t('group.members.aiUsageLoadFailed')}</div>}
      <div className="overflow-y-scroll pb-8" style={{ maxHeight: 'calc(100vh - 138px)' }}>
        <table className="w-full border-separate">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="text-left">
              <th className="border-y-2 py-2">{t('group.members.table.user')}</th>
              <th className="border-y-2 py-2">{t('group.members.table.role')}</th>
              <th className="border-y-2 py-2">{t('group.members.table.workspaces')}</th>
              {aiAssistantEnabled && (
                <th className="border-y-2 py-2">
                  <div className="flex items-center gap-2">
                    <IndeterminateCheckbox
                      className="form-checkbox size-5 border-brand"
                      checked={allDisplayedEnabled}
                      indeterminate={someDisplayedEnabled}
                      disabled={isSubmittingAI || displayedMemberIds.length === 0}
                      onChange={handleToggleAllAIAssistant}
                      aria-label={t('group.members.table.modelingAgent')}
                    />
                    <span>{t('group.members.table.modelingAgent')}</span>
                  </div>
                </th>
              )}
              {aiAssistantEnabled && <th className="border-y-2 py-2">{t('group.members.table.aiUsage')}</th>}
              <th className="border-y-2 p-2" />
            </tr>
          </thead>
          <tbody>
            {memberItems.length <= 0 ? (
              <tr>
                <td>{t('group.members.table.notFound')}</td>
              </tr>
            ) : (
              memberItems.map((i) => (
                <tr key={i.memberData.id} className="border-b-2">
                  <td className="border-b-2 border-gray-400 py-4 align-middle">
                    <div className="flex items-center">
                      <div className="mr-1 flex size-10 items-center justify-center">
                        <UserIcon
                          name={i.memberData.name}
                          id={i.memberData.id}
                          imageUrl={i.memberData.imageUrl}
                          isAnonymous={false}
                          size="large"
                          className="grow-0"
                        />
                      </div>
                      <div className="flex flex-col">
                        <Link
                          to={UserPagePathBuilder.groupConsoleMemberPage(group.id, i.memberData.id)}
                          className="text-lg font-bold text-brand underline"
                        >
                          {i.memberData.name}
                        </Link>
                        <span className="text-sm text-gray-600">{i.memberData.mailAddress}</span>
                      </div>
                    </div>
                  </td>
                  <td className="border-b-2 border-gray-400 py-4 align-middle">
                    {i.memberData.role === 'guest' ? (
                      <span>{t('group.members.roleGuest')}</span>
                    ) : (
                      <RoleSelect
                        groupId={group.id}
                        userId={i.memberData.id}
                        defaultRole={i.memberData.role}
                        disabled={currentUserId === i.memberData.id}
                      />
                    )}
                  </td>
                  <td className="border-b-2 border-gray-400 py-4 align-middle">{i.workspacesCount}</td>
                  {aiAssistantEnabled && (
                    <td className="border-b-2 border-gray-400 py-4 align-middle">
                      <input
                        type="checkbox"
                        className={classNames(
                          'form-checkbox size-5 border-brand',
                          isSubmittingAI ? 'cursor-not-allowed' : 'cursor-pointer'
                        )}
                        checked={memberEnabledSet.has(i.memberData.id)}
                        disabled={isSubmittingAI}
                        onChange={(e) => handleToggleMemberAIAssistant(i.memberData.id, e.target.checked)}
                        aria-label={t('group.members.table.modelingAgentForMember', { name: i.memberData.name })}
                      />
                    </td>
                  )}
                  {aiAssistantEnabled && (
                    <td className="border-b-2 border-gray-400 py-4 align-middle">
                      {memberEnabledSet.has(i.memberData.id)
                        ? renderAIUsageRatio(memberAIUsageRatios?.get(i.memberData.id))
                        : '-'}
                    </td>
                  )}
                  <td className="relative border-b-2 border-gray-400 py-4 align-middle">
                    {/* 自分は削除できない */}
                    <ItemMenuButton
                      memberData={i.memberData}
                      setRemovableMemberData={setRemovableMemberData}
                      disabled={currentUserId === i.memberData.id}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {removableMemberData && (
        <GroupMemberRemoveModal
          isOpen={!!removableMemberData}
          onClose={handleCloseGroupMemberRemoveModal}
          groupId={group.id}
          selectedMemberData={removableMemberData}
        />
      )}
    </>
  );
};
